import Button from '../../../../components/Button';
import GiftCard from '../../../../components/GiftCard';
import {
  useGiftForm,
} from '../../../../components/GiftFormContext';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectTokenByNetworkAndAddress } from '../../../../redux/slices/tokenSlice';
import TextInput from '../../../../components/Inputs/Text';
import { hapticsImpactLight } from '../../../../common/haptic';
import { Controller } from 'react-hook-form';
import { useCreateCustomGiftCardDesignMutation } from '../../../../redux/api/authorized/customGiftCardDesigns';
import { compressBase64Image } from '../../../../common/images';
import { openModal, closeModal } from '../../../../redux/slices/modals';
import { Capacitor } from '@capacitor/core';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import { selectPrivyToken } from '../../../../redux/slices/auth';
import {
  EVENT_GIFT_FLOW_CUSTOM_IMAGE_CREATED,
  EVENT_GIFT_FLOW_CUSTOMIZATION_COMPLETED,
  EVENT_GIFT_FLOW_MESSAGE_CHANGED,
  sendEvent,
  sendMetaEvent,
} from '../../../../common/analytics';
import StepWrapper from '../../../../components/StepWrapper';
import { StaggerContainer, StaggerItem } from '../../../../components/Stagger';
import { useFlag } from '@unleash/proxy-client-react';
import { useRef } from 'react';
import { handleKeyboardNext, handleKeyboardDone } from '../../../../components/GiftFormContext';

const Customize = ({
  onContinue,
  buttonLabel = 'Select Amount',
  isModal = false,
  currentStep = 0,
  isBack = false,
}) => {
  const { t } = useTranslation();
  const {
    usdAmount,
    image,
    setValue,
    trigger,
    targetTokenAddress,
    network,
    tokenAmount,
    delivery,
    control,
    errors,
    showFreeGift,
    senderName,
    lpd,
  } = useGiftForm();

  const targetToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const dispatch = useDispatch();
  const [createCustomGiftCardDesign] = useCreateCustomGiftCardDesignMutation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const isNative = Capacitor.isNativePlatform();
  const privyToken = useSelector(selectPrivyToken);
  const freeGiftAllInputs = useFlag('freeGiftFullInputs');

  const handleImageComplete = async (croppedDataUrl) => {
    hapticsImpactLight();
    const compressedImage = await compressBase64Image(croppedDataUrl);

    if (privyToken) {
      // User is logged in: save to backend and use returned URL
      const response = await createCustomGiftCardDesign({
        image: compressedImage,
        type: delivery === 'mail' ? 'physical' : 'digital',
      });

      if (response.data?.imageUrl) {
        // Set the image value - this will trigger GiftCard to update
        setValue('image', response.data.imageUrl, { shouldValidate: true });
        await trigger('image'); // Trigger validation to clear any errors
      }
    } else {
      // User is not logged in: use compressed image data URL directly
      setValue('image', compressedImage, { shouldValidate: true });
      await trigger('image'); // Trigger validation to clear any errors
    }

    // Close the crop modal
    dispatch(closeModal());
    sendEvent(EVENT_GIFT_FLOW_CUSTOM_IMAGE_CREATED, {
      is_free_gift: showFreeGift,
    });
  };

  const handleImageCancel = () => {
    dispatch(closeModal());
  };

  const handleImageSelection = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageData = reader.result;
      dispatch(
        openModal({
          name: 'cardImageCrop',
          props: {
            image: imageData,
            isPhysical: delivery === 'mail',
            onComplete: handleImageComplete,
            onClose: handleImageCancel,
          },
        }),
      );
    };
    reader.readAsDataURL(file);
  };

  const handleCreateCustomImage = () => {
    hapticsImpactLight();

    // On mobile/native: use native file picker
    if (isMobile || isNative) {
      // Create and trigger hidden file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      // Don't set capture attribute - allows user to choose between camera and photo library
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
          handleImageSelection(file);
        }
      };
      input.click();
    } else {
      // On web: open CustomizeCard modal with drag and drop
      dispatch(
        openModal({
          name: 'customizeCard',
          props: {
            isPhysical: delivery === 'mail',
            onComplete: handleImageComplete,
            onClose: handleImageCancel,
          },
        }),
      );
    }
  };

  const isValidForm =
    !errors.email?.message &&
    !errors.message?.message &&
    !errors.image?.message &&
    !errors.delivery?.message &&
    !errors.senderName?.message;

  // Refs for keyboard navigation - store IonInput elements for focus management
  const senderNameRef = useRef(null);
  const messageRef = useRef(null);

  return (
    <StepWrapper $back={isBack}>
        <StaggerContainer>
          <StaggerItem $delay={1}>
            <div style={{ width: '100%' }}>
              <GiftCard
                editable={true}
                hideValue
                usdAmount={usdAmount}
                tokenAmount={tokenAmount}
                cardImage={image}
                token={targetToken}
                isFreeGift={showFreeGift}
                onCardImageChange={(value) => setValue('image', value)}
                onCreateCustomImage={handleCreateCustomImage}
                isModal={isModal}
                currentStep={currentStep}
                lpd={lpd}
              />
            </div>
          </StaggerItem>
          <StaggerItem $delay={3}>
            <Controller
              name='senderName'
              control={control}
              render={({ field }) => (
                <TextInput
                  ref={(el) => {
                    field.ref(el);
                    senderNameRef.current = el;
                  }}
                  label={t('giftFlow.customize.fromOptional')}
                  inputMode='text'
                  type='text'
                  placeholder={t('giftFlow.customize.yourNamePlaceholder')}
                  error={errors?.senderName?.message}
                  enterkeyhint='next'
                  onKeyDown={(e) => handleKeyboardNext(e, messageRef)}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />
          </StaggerItem>
          <StaggerItem $delay={5}>
            <Controller
              name='message'
              control={control}
              render={({ field }) => (
                <TextInput
                  ref={(el) => {
                    field.ref(el);
                    messageRef.current = el;
                  }}
                  label={t('giftFlow.customize.personalMessageOptional')}
                  inputMode='text'
                  type='text'
                  placeholder={t('giftFlow.customize.writePersonalMessagePlaceholder')}
                  error={errors?.message?.message}
                  enterkeyhint='done'
                  onKeyDown={(e) => handleKeyboardDone(e, messageRef, {
                    onProceed: () => {
                      sendEvent(EVENT_GIFT_FLOW_CUSTOMIZATION_COMPLETED, {
                        is_free_gift: showFreeGift,
                        free_gift_all_inputs: freeGiftAllInputs,
                      });
                      onContinue();
                    },
                    isValidForm,
                  })}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    sendEvent(EVENT_GIFT_FLOW_MESSAGE_CHANGED, {
                      message: e.target.value,
                      is_free_gift: showFreeGift,
                    });
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />
          </StaggerItem>
          <StaggerItem $delay={7} style={{ marginTop: '20px', width: '100%' }}>
            <Button
              fullWidth
              clickAnimation='bouncy'
              onClick={() => {
                sendEvent(EVENT_GIFT_FLOW_CUSTOMIZATION_COMPLETED, {
                  is_free_gift: showFreeGift,
                  free_gift_all_inputs: freeGiftAllInputs,
                });
                if (targetToken && usdAmount) {
                  const numericValue = parseFloat(usdAmount) || 0;
                  sendMetaEvent('AddToCart', {
                    content_ids: [targetToken.address],
                    content_type: 'product',
                    content_name: targetToken?.name,
                    currency: 'USD',
                    value: numericValue,
                    num_items: 1,
                  });
                }
                onContinue();
              }}
              disabled={!isValidForm}
            >
              {buttonLabel}
            </Button>
          </StaggerItem>
        </StaggerContainer>
      </StepWrapper>
  );
};

export default Customize;
